import { useCallback, useEffect, useRef, useState } from 'react';
import { prefsFromPrintDraft, printDraftFromPrefs, readPrintDraft, writePrintDraft } from './printDrafts';

export default function useB4PrintDraft({ userId, context, problemIds, sharedPrefs }) {
  const [state, setState] = useState(() => {
    const loaded = context ? readPrintDraft(userId, context) : { draft: sharedPrefs, status: 'new' };
    return { data: loaded.draft, edited: false, initialStatus: loaded.status };
  });
  const [status, setStatus] = useState(state.initialStatus);
  const pending = useRef(null);
  const timer = useRef(null);
  const prefs = context ? prefsFromPrintDraft(state.data, problemIds, sharedPrefs) : state.data;

  const update = useCallback((patch) => {
    setState((prev) => {
      const current = context ? prefsFromPrintDraft(prev.data, problemIds, sharedPrefs) : prev.data;
      const next = { ...current, ...patch,
        patPreset: { ...current.patPreset, ...(patch.patPreset || {}) } };
      return { ...prev, data: context ? printDraftFromPrefs(next, problemIds) : next, edited: true };
    });
  }, [context, problemIds, sharedPrefs]);

  const flush = useCallback((showStatus = true) => {
    clearTimeout(timer.current);
    if (!context || !pending.current) return;
    const result = writePrintDraft(userId, context, pending.current);
    pending.current = null;
    if (showStatus) setStatus(result);
  }, [userId, context]);

  useEffect(() => {
    if (!context || !state.edited) return undefined;
    pending.current = state.data;
    setStatus('saving');
    timer.current = setTimeout(flush, 400);
    return () => clearTimeout(timer.current);
  }, [context, state, flush]);

  useEffect(() => {
    const onPageHide = () => flush();
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      flush(false);
    };
  }, [flush]);

  return { prefs, update, status, flush };
}
